<template>
  <v-card flat class="pgs-ai-card rounded-xl mb-6">
    <!-- Header -->
    <div class="pgs-ai-header pa-4 d-flex align-center justify-space-between flex-wrap gap-2">
      <div class="d-flex align-center">
        <div class="pgs-ai-avatar mr-3">
          <v-icon size="20" color="white">mdi-creation</v-icon>
        </div>
        <div>
          <div class="d-flex align-center">
            <h2 class="text-subtitle-1 font-weight-bold pgs-text-primary mb-0 mr-2">{{ title }}</h2>

          </div>
          <p class="text-caption pgs-text-secondary mb-0">{{ subtitle }}</p>
        </div>
      </div>

      <div class="d-flex align-center">
        <!-- Switch Context -->


        <!-- Clear Chat Button -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small class="mr-1" v-bind="attrs" v-on="on" :disabled="messages.length <= 1" @click="clearChat">
              <v-icon size="18" color="grey darken-1">mdi-delete-sweep-outline</v-icon>
            </v-btn>
          </template>
          <span>Limpiar historial de conversación</span>
        </v-tooltip>

        <!-- Toggle Collapse Button -->
        <v-btn icon small @click="isExpanded = !isExpanded">
          <v-icon size="20" color="grey darken-2">
            {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
          </v-icon>
        </v-btn>
      </div>
    </div>


    <!-- Development Warning Banner -->
    <div v-show="isExpanded" class="px-4 mt-2 mb-0">
      <div class="pa-3 rounded-lg d-flex align-center" style="background-color: #FFFBEB; border: 1px solid #FDE68A;">
        <v-icon color="#D97706" size="20" class="mr-2">mdi-tools</v-icon>
        <span class="text-body-2" style="color: #92400E;">
          <strong>Próximamente:</strong> Esta función de Asistente IA se encuentra actualmente en proceso de desarrollo.
        </span>
      </div>
    </div>

    <!-- Collapsible Chat Body -->
    <v-expand-transition>
      <div v-show="isExpanded">
        <!-- Divider -->
        <div class="pgs-ai-divider"></div>

        <!-- Chat Conversation Area -->
        <div style="filter: blur(1px); opacity: 0.7; pointer-events: none; user-select: none;">
          <div class="pgs-ai-chat-body pa-4" ref="chatBody">
            <div v-for="(msg, index) in messages" :key="msg.id || index"
              :class="['pgs-chat-msg-row mb-4 d-flex', msg.role === 'user' ? 'justify-end' : 'justify-start']">
              <!-- Assistant Avatar -->
              <div v-if="msg.role === 'assistant'" class="pgs-avatar-sm mr-2 mt-1">
                <v-icon size="14" color="white">mdi-creation</v-icon>
              </div>

              <!-- Message Bubble -->
              <div :class="[
                'pgs-chat-bubble pa-3 rounded-xl',
                msg.role === 'user' ? 'pgs-bubble-user' : 'pgs-bubble-assistant',
                msg.isError ? 'pgs-bubble-error' : ''
              ]">
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="pgs-msg-author font-weight-bold">
                    {{ msg.role === 'user' ? 'Tú' : 'Asistente IA' }}
                  </span>
                  <div class="d-flex align-center ml-3">
                    <span class="pgs-msg-time text-caption">{{ msg.timestamp }}</span>
                    <v-btn v-if="msg.role === 'assistant' && !msg.isInitial" icon x-small class="ml-1"
                      @click="copyMessage(msg.text, index)"
                      :title="copiedIndex === index ? '¡Copiado!' : 'Copiar texto'">
                      <v-icon size="13" :color="copiedIndex === index ? 'success' : 'grey'">
                        {{ copiedIndex === index ? 'mdi-check' : 'mdi-content-copy' }}
                      </v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Message Text -->
                <div class="pgs-msg-content text-body-2" style="white-space: pre-wrap;">{{ msg.text }}</div>

                <!-- Context Pill Tag if message used live data -->
                <div v-if="msg.usedContext" class="mt-2 pt-1 border-top-dashed">
                  <span class="pgs-used-context-tag">
                    <v-icon size="11" color="#3B82F6" class="mr-1">mdi-database-check</v-icon>
                    Respaldado con datos en vivo del club
                  </span>
                </div>
              </div>

              <!-- User Avatar -->
              <div v-if="msg.role === 'user'" class="pgs-avatar-sm pgs-avatar-user ml-2 mt-1">
                <v-icon size="14" color="white">mdi-account</v-icon>
              </div>
            </div>

            <!-- Loading Indicator (Thinking / Typing) -->
            <div v-if="loading" class="pgs-chat-msg-row mb-4 d-flex justify-start align-center">
              <div class="pgs-avatar-sm mr-2">
                <v-icon size="14" color="white">mdi-creation</v-icon>
              </div>
              <div class="pgs-chat-bubble pgs-bubble-assistant pa-3 rounded-xl d-flex align-center">
                <span class="text-caption pgs-text-secondary mr-2">Analizando y generando respuesta</span>
                <div class="pgs-typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Prompt Suggestions Chips -->
          <div class="px-4 pb-2 pt-1 d-flex align-center flex-wrap gap-2">
            <span class="text-caption font-weight-medium pgs-text-muted mr-1">Sugerencias:</span>
            <v-chip v-for="(starter, idx) in promptStarters" :key="idx" small outlined color="#3B82F6"
              class="pgs-starter-chip mr-2 mb-2" @click="selectPromptStarter(starter.text)" :disabled="true">
              <v-icon left size="14">{{ starter.icon }}</v-icon>
              {{ starter.label }}
            </v-chip>
          </div>

          <!-- Prompt Input Bar -->
          <div class="pa-4 pt-2">
            <div class="pgs-prompt-bar d-flex align-center pa-2 rounded-xl">
              <v-textarea v-model="promptInput" :placeholder="'Módulo en desarrollo...'" rows="1" auto-grow hide-details
                dense flat solo class="pgs-prompt-textarea pt-0" @keydown.enter.exact.prevent="submitPrompt"
                :disabled="true"></v-textarea>

              <v-btn color="primary" depressed fab x-small class="ml-2 flex-shrink-0" :loading="loading"
                :disabled="true" @click="submitPrompt">
                <v-icon size="16">mdi-arrow-up</v-icon>
              </v-btn>
            </div>
            <div class="d-flex justify-space-between align-center px-2 mt-1">
              <span class="text-caption pgs-text-muted" style="font-size: 11px;">
                Presiona <strong>Enter</strong> para enviar, <strong>Shift+Enter</strong> para salto de línea
              </span>
              <span v-if="endpoint" class="text-caption pgs-text-muted" style="font-size: 10px;">
                Endpoint: {{ endpoint }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { mapGetters } from "vuex";

export default {
  name: "AiPromptChat",
  props: {
    endpoint: {
      type: String,
      default: ""
    },
    contextData: {
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: "Asistente Inteligente de Operaciones"
    },
    subtitle: {
      type: String,
      default: "Realiza preguntas y pide análisis de las operaciones del club en lenguaje natural"
    },
    placeholder: {
      type: String,
      default: "Pregúntale a la IA sobre jugadores, ingresos, proyecciones u operaciones..."
    }
  },
  data() {
    return {
      isExpanded: true,
      promptInput: "",
      loading: false,
      includeContext: true,
      copiedIndex: null,
      messages: [
        {
          id: 1,
          role: "assistant",
          text: "¡Hola! Soy tu asistente de inteligencia artificial para Par Golf. Puedo analizar las reservas, salidas del día, balance de ingresos o responder consultas operativas. ¿En qué te puedo ayudar hoy?",
          timestamp: moment().format("hh:mm A"),
          isInitial: true
        }
      ],
      promptStarters: [
        {
          icon: "mdi-chart-bar",
          label: "Resumen del día",
          text: "¿Puedes darme un resumen ejecutivo del estado actual de las operaciones de hoy (jugadores, cobrados y pendientes)?"
        },
        {
          icon: "mdi-cash-multiple",
          label: "Balance de Ingresos",
          text: "Analiza los ingresos proyectados en MXN y USD. ¿Cuál es el promedio por jugador y qué porcentaje está cobrado?"
        },
        {
          icon: "mdi-clock-alert-outline",
          label: "Salidas pendientes",
          text: "¿Cuántas reservas tenemos aún pendientes de cobro o confirmación para las próximas horas?"
        },
        {
          icon: "mdi-lightbulb-on-outline",
          label: "Sugerencias operativas",
          text: "Basado en el clima y las salidas programadas, ¿qué sugerencias operativas tienes para el staff hoy?"
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["getFirebaseApp"])
  },
  methods: {
    selectPromptStarter(text) {
      this.promptInput = text;
      this.submitPrompt();
    },

    clearChat() {
      this.messages = [
        {
          id: Date.now(),
          role: "assistant",
          text: "Historial limpiado. ¿Qué otra consulta deseas realizar?",
          timestamp: moment().format("hh:mm A"),
          isInitial: true
        }
      ];
    },

    copyMessage(text, index) {
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(text).then(() => {
        this.copiedIndex = index;
        setTimeout(() => {
          if (this.copiedIndex === index) {
            this.copiedIndex = null;
          }
        }, 2000);
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatBody;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    async submitPrompt() {
      const query = this.promptInput ? this.promptInput.trim() : "";
      if (!query || this.loading) return;

      const userTime = moment().format("hh:mm A");
      this.messages.push({
        id: Date.now(),
        role: "user",
        text: query,
        timestamp: userTime
      });

      this.promptInput = "";
      this.loading = true;
      this.scrollToBottom();

      const payload = {
        prompt: query,
        timestamp: new Date().toISOString(),
        usedContext: this.includeContext,
        context: this.includeContext ? this.contextData : {}
      };

      this.$emit("send-prompt", payload);

      try {
        let aiResponseText = "";

        // 1. Si hay un endpoint HTTP/HTTPS explícito proporcionado
        if (this.endpoint && (this.endpoint.startsWith("http://") || this.endpoint.startsWith("https://"))) {
          const res = await axios.post(this.endpoint, payload, {
            timeout: 30000
          });
          aiResponseText =
            (res.data && (res.data.text || res.data.message || res.data.response || res.data.answer)) ||
            (typeof res.data === "string" ? res.data : JSON.stringify(res.data));
        }
        // 2. Si el endpoint es una función de Firebase Cloud Functions o se usa fba
        else if (
          this.getFirebaseApp &&
          this.getFirebaseApp.fba &&
          typeof this.getFirebaseApp.fba.call === "function" &&
          this.endpoint
        ) {
          const res = await this.getFirebaseApp.fba.call(this.endpoint, payload);
          aiResponseText =
            (res && (res.text || res.message || res.response || res.answer)) ||
            (typeof res === "string" ? res : JSON.stringify(res));
        }
        // 3. Fallback inteligente con datos del club en caso de no estar desplegado aún el endpoint
        else {
          aiResponseText = await this.generateSmartMockResponse(query, payload.context);
        }

        this.messages.push({
          id: Date.now() + 1,
          role: "assistant",
          text: aiResponseText,
          timestamp: moment().format("hh:mm A"),
          usedContext: this.includeContext
        });

        this.$emit("response", {
          prompt: query,
          response: aiResponseText,
          payload
        });
      } catch (err) {
        console.error("Error al enviar prompt a la IA:", err);

        // Fallback de asistencia con datos locales si la llamada de red falló
        const fallback = await this.generateSmartMockResponse(query, payload.context, true);

        this.messages.push({
          id: Date.now() + 1,
          role: "assistant",
          text: fallback,
          timestamp: moment().format("hh:mm A"),
          isError: false,
          usedContext: this.includeContext
        });
      } finally {
        this.loading = false;
        this.scrollToBottom();
      }
    },

    /**
     * Respuesta contextual e inteligente basada en las métricas en vivo del club
     * Útil mientras se conecta el endpoint definitivo o como fallback de alta fidelidad.
     */
    async generateSmartMockResponse(query, context, wasNetworkFallback = false) {
      await new Promise((resolve) => setTimeout(resolve, 800));

      const stats = (context && context.stats) || {};
      const weather = (context && context.weather) || null;
      const lowerQuery = query.toLowerCase();

      let prefix = "";
      if (wasNetworkFallback && this.endpoint) {
        prefix = `*(Nota: No se pudo conectar al endpoint '${this.endpoint}'. Analizando con los datos locales del sistema:)*\n\n`;
      }

      // Consulta de Ingresos / Balance
      if (lowerQuery.includes("ingreso") || lowerQuery.includes("dinero") || lowerQuery.includes("usd") || lowerQuery.includes("mxn")) {
        const revMXN = stats.revenueMXN ? Number(stats.revenueMXN).toLocaleString("es-MX", { minimumFractionDigits: 2 }) : "0.00";
        const paidMXN = stats.paidRevenueMXN ? Number(stats.paidRevenueMXN).toLocaleString("es-MX", { minimumFractionDigits: 2 }) : "0.00";
        const revUSD = stats.revenueUSD ? Number(stats.revenueUSD).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "0.00";
        const paidUSD = stats.paidRevenueUSD ? Number(stats.paidRevenueUSD).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "0.00";
        const ticketMXN = stats.avgTicketMXN ? Number(stats.avgTicketMXN).toFixed(2) : "0.00";

        return `${prefix}💰 **Análisis Financiero del Día:**\n\n` +
          `• **Moneda Nacional (MXN):** Total proyectado de **$${revMXN} MXN**, con un cobro efectivo de **$${paidMXN} MXN**.\n` +
          `• **Moneda Extranjera (USD):** Total proyectado de **$${revUSD} USD**, con **$${paidUSD} USD** ya cobrados.\n` +
          `• **Ticket Promedio:** Estimado en **$${ticketMXN} MXN** por jugador.\n\n` +
          `💡 *Recomendación:* Dar seguimiento en recepción a las reservas que aún registran saldo pendiente antes de su turno en el tee sheet.`;
      }

      // Consulta de Jugadores / Ocupación
      if (lowerQuery.includes("jugador") || lowerQuery.includes("pax") || lowerQuery.includes("resumen") || lowerQuery.includes("ocupaci")) {
        const pax = stats.players || 0;
        const res = stats.reservations || 0;
        const paid = stats.paid || 0;
        const pending = stats.pending || 0;
        const canceled = (stats.canceled || 0) + (stats.noShow || 0);

        return `${prefix}📊 **Resumen de Jugadores y Salidas:**\n\n` +
          `• **Total de Jugadores (Pax):** **${pax}** jugadores registrados en **${res}** salidas hoy.\n` +
          `• **Distribución de Estatus:**\n` +
          `   - ✅ **Pagados:** ${paid} jugadores\n` +
          `   - ⏳ **Pendientes:** ${pending} jugadores\n` +
          `   - ❌ **Cancelados / No Show:** ${canceled} jugadores\n\n` +
          `El ritmo actual muestra una ocupación activa. Puedes revisar el desglose en el Tee Sheet interactivo.`;
      }

      // Salidas / Salidas pendientes
      if (lowerQuery.includes("salida") || lowerQuery.includes("tee") || lowerQuery.includes("hora") || lowerQuery.includes("pendiente")) {
        const pending = stats.pending || 0;
        return `${prefix}⛳ **Estado de Salidas y Reservas:**\n\n` +
          `Actualmente hay **${pending} jugadores pendientes** de liquidación o confirmación en recepción.\n` +
          `Te sugiero verificar la tabla de **Próximas Salidas** para consultar los folios y nombres de los titulares asignados a los siguientes turnos.`;
      }

      // Clima / Operaciones
      if (lowerQuery.includes("clima") || lowerQuery.includes("tiempo") || lowerQuery.includes("lluvia") || lowerQuery.includes("sugerencia")) {
        const temp = weather && weather.temperature ? `${weather.temperature}°C` : "condiciones óptimas";
        return `${prefix}🌤️ **Recomendaciones Operativas:**\n\n` +
          `• Temperatura registrada: **${temp}**.\n` +
          `• Mantener hidratación adecuada y carritos de golf listos en la zona de salida.\n` +
          `• Supervisar el intervalo entre grupos en los primeros 9 hoyos para evitar demoras en el ritmo de juego.`;
      }

      // Respuesta general
      const totalPax = stats.players || 0;
      return `${prefix}He analizado tu consulta *"__${query}__"*.\n\n` +
        `Actualmente el club cuenta con **${totalPax} jugadores programados para hoy**. Puedes pedirme análisis de ingresos, estado de pagos por tarifa o previsiones operativas. ¿Deseas profundizar en algún punto específico?`;
    }
  }
};
</script>

<style scoped>
.pgs-ai-card {
  background: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 4px 12px -2px rgba(15, 23, 42, 0.05), 0 2px 4px -2px rgba(15, 23, 42, 0.03) !important;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.pgs-ai-card:hover {
  border-color: #CBD5E1 !important;
}

.pgs-ai-header {
  background: linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%);
}

.pgs-ai-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
}

.pgs-ai-badge-online {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: #EFF6FF;
  border: 1px solid #DBEAFE;
  color: #2563EB;
  font-size: 11px;
  font-weight: 600;
}

.pgs-ai-badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #3B82F6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
  animation: aiPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes aiPulse {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.4;
    transform: scale(1.15);
  }
}

.pgs-context-pill {
  background-color: #F1F5F9;
  border: 1px solid #E2E8F0;
}

.pgs-ai-divider {
  height: 1px;
  background-color: #F1F5F9;
}

.pgs-ai-chat-body {
  max-height: 340px;
  min-height: 140px;
  overflow-y: auto;
  background-color: #FAFAFA;
  display: flex;
  flex-direction: column;
}

.pgs-chat-msg-row {
  animation: fadeInMsg 0.25s ease forwards;
}

@keyframes fadeInMsg {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pgs-avatar-sm {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pgs-avatar-user {
  background: #0F172A !important;
}

.pgs-chat-bubble {
  max-width: 82%;
  position: relative;
  word-break: break-word;
}

.pgs-bubble-assistant {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  color: #1E293B;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.pgs-bubble-user {
  background: #0F172A;
  color: #FFFFFF;
}

.pgs-bubble-user .pgs-msg-author {
  color: #94A3B8;
}

.pgs-bubble-user .pgs-msg-time {
  color: #64748B;
}

.pgs-bubble-error {
  border-color: #FCA5A5 !important;
  background-color: #FEF2F2 !important;
}

.pgs-msg-author {
  font-size: 11px;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.pgs-msg-time {
  font-size: 10px;
  color: #94A3B8;
}

.border-top-dashed {
  border-top: 1px dashed #E2E8F0;
}

.pgs-used-context-tag {
  font-size: 10px;
  font-weight: 500;
  color: #3B82F6;
  display: inline-flex;
  align-items: center;
}

.pgs-prompt-bar {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  transition: all 0.2s ease;
}

.pgs-prompt-bar:focus-within {
  background: #FFFFFF;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.pgs-prompt-textarea>>>textarea {
  font-size: 14px;
  line-height: 1.4;
  color: #0F172A;
}

.pgs-prompt-textarea>>>textarea::placeholder {
  color: #94A3B8;
  font-size: 13px;
}

.pgs-starter-chip {
  background-color: #FFFFFF !important;
  border-color: #E2E8F0 !important;
  color: #475569 !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  transition: all 0.2s ease;
}

.pgs-starter-chip:hover {
  background-color: #EFF6FF !important;
  border-color: #93C5FD !important;
  color: #1D4ED8 !important;
  transform: translateY(-1px);
}

.pgs-typing-dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pgs-typing-dots span {
  width: 5px;
  height: 5px;
  background-color: #94A3B8;
  border-radius: 50%;
  animation: typingBounce 1.2s infinite ease-in-out both;
}

.pgs-typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.pgs-typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typingBounce {

  0%,
  80%,
  100% {
    transform: scale(0);
  }

  40% {
    transform: scale(1);
  }
}
</style>
